import type {
  WebsiteReadResponse,
  WebsiteTemplateRegistryTemplateSummary,
} from 'src/api/generated';

/** Design a new website starts with. It is also the recommended design. */
export const DEFAULT_WEBSITE_TEMPLATE_KEY = 'vitrina-v1';

/** Registry keys of the designs shown first and tagged as recommended. */
export const RECOMMENDED_WEBSITE_TEMPLATE_KEYS: readonly string[] = [
  DEFAULT_WEBSITE_TEMPLATE_KEY,
];

/**
 * Registry keys of earlier designs that newer ones replace. They stay public
 * so the sites built on them keep working, but only agencies that use them
 * see them in the picker.
 */
export const LEGACY_WEBSITE_TEMPLATE_KEYS: readonly string[] = [
  'real-estate-catalog-v1',
  'comos-onix-v1',
  'comos-cuarzo-v1',
];

/** Days during which a newly registered design keeps its "new" tag. */
export const WEBSITE_TEMPLATE_NEW_BADGE_DAYS = 30;

const DAY_IN_MS = 24 * 60 * 60 * 1000;

/** Highlight shown next to a design name in the picker. */
export type WebsiteTemplateBadge = 'recommended' | 'new';

/**
 * Checks whether a design is one Comos recommends to every agency.
 *
 * @param template - Selectable registry design.
 * @returns Whether the design key is in the recommended list.
 */
export const isRecommendedWebsiteTemplate = (
  template: Pick<WebsiteTemplateRegistryTemplateSummary, 'key'>
): boolean => RECOMMENDED_WEBSITE_TEMPLATE_KEYS.includes(template.key);

/**
 * Resolves the highlight for a design card.
 *
 * Recommended designs always show "recommended". Legacy designs show none.
 * Other designs show "new" while their registry entry is at most
 * {@link WEBSITE_TEMPLATE_NEW_BADGE_DAYS} days old.
 *
 * @param template - Selectable registry design.
 * @param now - Reference time, injected for deterministic tests.
 * @returns The badge to show, or `undefined` when the card needs none.
 */
export const resolveWebsiteTemplateBadge = (
  template: Pick<WebsiteTemplateRegistryTemplateSummary, 'key' | 'createdAt'>,
  now: Date
): WebsiteTemplateBadge | undefined => {
  if (isRecommendedWebsiteTemplate(template)) {
    return 'recommended';
  }
  if (LEGACY_WEBSITE_TEMPLATE_KEYS.includes(template.key)) {
    return undefined;
  }

  const createdAt = template.createdAt
    ? Date.parse(template.createdAt)
    : Number.NaN;
  if (Number.isNaN(createdAt)) {
    return undefined;
  }

  return now.getTime() - createdAt <=
    WEBSITE_TEMPLATE_NEW_BADGE_DAYS * DAY_IN_MS
    ? 'new'
    : undefined;
};

/**
 * Keeps the designs an agency can pick: every current design, plus a legacy
 * one while the saved website or the draft uses it.
 *
 * @param templates - Selectable registry designs.
 * @param usedTemplateKeys - Design keys of the saved website and the draft.
 * @returns A new array without the legacy designs the agency does not use.
 */
export const filterWebsiteTemplatesForPicker = (
  templates: WebsiteTemplateRegistryTemplateSummary[],
  usedTemplateKeys: readonly (string | null | undefined)[]
): WebsiteTemplateRegistryTemplateSummary[] =>
  templates.filter(
    (template) =>
      !LEGACY_WEBSITE_TEMPLATE_KEYS.includes(template.key) ||
      usedTemplateKeys.includes(template.key)
  );

/**
 * Orders designs for the picker: recommended designs first, then the registry
 * order.
 *
 * @param templates - Selectable registry designs.
 * @returns A new array; the input is not mutated.
 */
export const sortWebsiteTemplatesForPicker = (
  templates: WebsiteTemplateRegistryTemplateSummary[]
): WebsiteTemplateRegistryTemplateSummary[] =>
  [...templates].sort(
    (left, right) =>
      Number(isRecommendedWebsiteTemplate(right)) -
      Number(isRecommendedWebsiteTemplate(left))
  );

/**
 * Checks whether a design is the saved selection of a website that has been
 * published at least once.
 *
 * The website record keeps a single template selection, and deployments do not
 * expose theirs, so after a saved but unpublished design change this marks the
 * saved design.
 *
 * @param template - Selectable registry design.
 * @param website - Current website, when one exists.
 * @returns Whether the card should show the "in use" tag.
 */
export const isWebsiteTemplateInUse = (
  template: Pick<
    WebsiteTemplateRegistryTemplateSummary,
    'key' | 'version' | 'registryTemplateId'
  >,
  website: WebsiteReadResponse | null | undefined
): boolean =>
  Boolean(website?.publishedConfig) &&
  template.key === website?.templateKey &&
  template.version === website.templateVersion &&
  (!website.registryTemplateId ||
    template.registryTemplateId === website.registryTemplateId);
